import { Component, type ComponentChildren } from 'preact';
import { trackCaptureEvent } from '../lib/captureAnalytics';
import { currentRoute, describeError } from '../lib/errorReporting';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ComponentChildren;
}

interface State {
  hasError: boolean;
}

/**
 * Catches a render-time throw anywhere under the router.
 *
 * Without this, one exception in one component unmounts the whole tree and the
 * guest lands on a blank white page — on the acquisition funnel, from an
 * invite link, with no way back. A retry that just re-renders is usually
 * enough (a transient data shape, a failed lazy chunk), so offer that before
 * asking anyone to reload.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    const described = describeError(error);
    trackCaptureEvent('render_error', {
      surface: 'app',
      message: described.message,
      stack: described.stack,
      errorName: described.name,
      route: currentRoute(),
    });
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div class={styles.container} role="alert">
        <h1 class={styles.title}>That didn&rsquo;t load right</h1>
        <p class={styles.body}>
          Something on our end broke. Your takes are safe — nothing was lost.
        </p>
        <div class={styles.actions}>
          <button type="button" class={styles.primary} onClick={this.handleRetry}>
            Try again
          </button>
          <button type="button" class={styles.secondary} onClick={this.handleReload}>
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
